import moment from "moment"
import { useContext, useEffect, useState } from "react"
import queryString from 'query-string'

import useCallApi from "../../../hooks/useCallApi"
import Dollars from "../../../templates/Dollars/Dollars"
import Number from "../../../templates/Number/Number"
import Loading from "../../../templates/UI/Loading/Loading"
import styles from './BizdevPipelineWeekly.module.scss'
import callAPI from "../../../helpers/callAPI"
import { SystemMessageContext } from "../../../context/SystemMessageContext"
import Graph from "../../../components/Graph/Graph"
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min"
import ReactTooltip from "react-tooltip"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const BizdevPipelineWeekly = () => {
    const { data, dataLoaded, refetch } = useCallApi(`reports/bizdev_pipeline_weekly`, 'GET', {}, [])

    document.title = "Bizdev Pipeline Weekly Report"

    const location = useLocation()
    const history = useHistory()

    let urlParams = queryString.parse(location.search)

    const [closeRate, setCloseRate] = useState({})
    const [year, setYear] = useState(urlParams.year ? parseInt(urlParams.year) : moment().year())

    const { showSystemMessage } = useContext(SystemMessageContext)

    useEffect(() => {
        refetch()
    }, [location, year])

    const handleInputChange = (e, iso_week_id) => {
        e.target.setCustomValidity('')
        setCloseRate({ iso_week_id, close_rate: e.target.value })
    }

    const updateYear = (e) => {
        e.preventDefault()
        setYear(e.target.value)
        history.push(`?year=${e.target.value}`)
    }

    const handleCloseRateSubmit = (e) => {
        e.preventDefault()

        let close_rate = closeRate["close_rate"] === undefined ? parseFloat(e.target.close_rate.value) : closeRate["close_rate"]
        let current_week_id = closeRate["iso_week_id"] === undefined ? moment().isoWeek() : closeRate["iso_week_id"]

        if (isNaN(close_rate) || close_rate < 0 || close_rate > 1) {
            e.target.close_rate.setCustomValidity('Please enter a valid close rate between 0 and 1')
            e.target.close_rate.reportValidity()
            return
        }

        // update the close rate for the current week
        callAPI(`reports/updateCloseRate/${current_week_id}`, 'PUT', { close_rate: close_rate })
            .then(result => {
                if (result['success'] === true) {
                    showSystemMessage('success', result.message)
                    refetch()
                } else if (result['success'] === false) {
                    showSystemMessage('error', result.errors.message)
                }
            })
            .catch(error => {
                console.log(`Error while updating close rate: ${error}`)
                showSystemMessage('error', 'An error occurred while updating close rate')
            })
    }

    let columns = [
        { header: "", key: "date_range" },
        { header: "KPI", key: "iso_week_id" },
        { header: 'Year', key: 'year' },
        { header: "New Leads Added", key: "kpi_new_leads_added" },
        { header: "Total New Leads in System", key: "kpi_tot_new_leads_in_system" },
        { header: "Discussion", key: "kpi_discussion" },
        { header: "Negotiation", key: "kpi_negotiation" },
        { header: "Total Advertisers in Pipeline", key: "total_adv_in_pipeline" },
        { header: "Close Rate", key: "close_rate" },
        { header: "Estimated Pipeline Value (monthly)", key: "adv_monthly_earnings" },
        { header: "Active Advertisers with Active Campaigns", key: "kpi_active_adv_with_active_campaigns" },
        { header: "Active Campaigns", key: "kpi_active_campaigns" },
        { header: 'Promoted Active in Week', key: 'promoted_active_in_week' }
    ]


    let graphData

    let labels = []
    let dataset = []

    dataLoaded && data.report.filter(record => record.year == year).forEach(record => {
        labels.push(`Week ${record.iso_week_id}`)
        dataset.push(record.adv_monthly_earnings)
    })

    graphData = {
        labels: labels,
        datasets: [
            {
                data: dataset,
                backgroundColor: 'rgba(23,162, 184, 0.1)',
                borderColor: 'rgba(23, 162, 184, 1)',
                fill: false,
            },
        ],
    }

    return (
        <div>
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Reports</h1>
            </div>

            <div className={`card ${styles.tableCard}`}>
                <div className={`d-flex justify-content-start align-items-center card-header`}>
                    <div className="d-flex">
                        <h1 className="mb-h1-dark">Bizdev Pipeline Weekly</h1>
                    </div>
                    <div className="d-flex">
                        <select className="form-control ml-4" value={year} onChange={updateYear}>
                            {dataLoaded && [...new Set(data.report.map(record => record.year))].map(year => {
                                return <option value={year} key={year}>{year}</option>
                            })}
                        </select>
                    </div>
                </div>
                <div className="card-body p-0">
                    {dataLoaded ? data.report.filter(record => record.year == year).length === 0 ? <div className="p-3">No stats for the current parameters.</div> :
                        <>
                            <div className={styles.tableScroll}>
                                <ReactTooltip />
                                <table className={`table table-striped table-hover table-bordered ${styles.compactTable}`}>
                                    <tbody>
                                        {columns.map(col => (
                                            <tr key={col.key}>
                                                <th className={styles.stickyCol}>{col.key === 'promoted_active_in_week' ? <span>{col.header}<FontAwesomeIcon className="ml-2 text-primary" icon={"question-circle"} data-tip={'Promoted Active in Week number updates DAILY for the CURRENT WEEK.'} /></span> : col.header}</th>
                                                {
                                                    data.report.filter(record => record.year == year).map((record, index) => {

                                                        // If it's the close rate column, we want to show 2 decimals, otherwise 0 decimals. If it's the adv_monthly_earnings column, we want to show it as dollars
                                                        let outputField = <Number value={record[col.key]} decimals={col.key === 'close_rate' ? 2 : 0} />

                                                        if (col.key === 'year' || col.key === 'date_range') {
                                                            outputField = record[col.key]
                                                        } else if (col.key === 'adv_monthly_earnings') {
                                                            outputField = <Dollars value={record[col.key]} />
                                                        } else if (col.key === 'close_rate' && (moment().isoWeek() === record.iso_week_id && moment().year() === record.year)) {
                                                            outputField = <Number value={record[col.key]} decimals={2} />
                                                            outputField = <form className="d-flex justify-content-end" onSubmit={handleCloseRateSubmit}><input name='close_rate' defaultValue={record[col.key]} className={`form-control ${styles.editInput}`} onChange={(e) => handleInputChange(e, record.iso_week_id)} /></form>
                                                        } else if (col.key === 'promoted_active_in_week' && (moment().isoWeek() === record.iso_week_id && moment().year() === record.year)) {
                                                            outputField = <Number value={record[col.key]} decimals={2} />
                                                            outputField = <span className="d-flex justify-content-end text-success font-weight-bold" data-tip={`Updated Daily`}>{record[col.key]}</span>
                                                        }

                                                        return (
                                                            <td key={index} className={col.key === 'iso_week_id' ? 'font-weight-bold text-right' : col.key === 'date_range' ? 'text-secondary text-right text-small' : 'text-right'}>{col.key === 'iso_week_id' ? `Week ${record[col.key]}` : outputField}</td>
                                                        )
                                                    })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className={styles.graphContainer} style={{ minHeight: '150px' }}>
                                <Graph data={graphData} />
                            </div>
                        </>
                        : <Loading></Loading>}
                </div>
            </div>
        </div>
    )
}

export default BizdevPipelineWeekly